import { Link } from "react-router-dom";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { 
  Shield, Clock, Users, Star, ArrowRight,
  Award, TrendingUp, Heart, HelpCircle, CheckCircle, FileText, PhoneCall
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import TestimonialsCarousel from "@/components/TestimonialsCarousel";
import ExitIntentPopup from "@/components/ExitIntentPopup";
import HeroSection from "@/components/home/HeroSection";
import MovingCostCalculator from "@/components/home/MovingCostCalculator";
import PopularCitiesSection from "@/components/home/PopularCitiesSection";
import TopCompaniesSection from "@/components/home/TopCompaniesSection";
import { useReviews } from "@/hooks/use-reviews";
import { usePrerenderReady } from "@/hooks/use-prerender-ready";
import PopularSearches from "@/components/PopularSearches";

const Index = () => {
  const { data: dynamicTestimonials, isLoading: isLoadingReviews } = useReviews(12);
  usePrerenderReady(!isLoadingReviews);

  const steps = [
    { 
      number: "1", 
      icon: FileText,
      title: "Beschrijf jouw verhuizing", 
      description: "Vul je huidige en nieuwe postcode in, kies een verhuisdatum en geef aan wat er mee moet. Kost je minder dan 2 minuten." 
    },
    { 
      number: "2", 
      icon: PhoneCall,
      title: "Verhuizers nemen contact op", 
      description: "Binnen 24 uur ontvang je tot 5 verhuisoffertes van bedrijven die beschikbaar zijn in jouw regio." 
    },
    { 
      number: "3", 
      icon: CheckCircle,
      title: "Vergelijk en bespaar", 
      description: "Bekijk de verhuisoffertes naast elkaar, vergelijk op prijs en reviews, en kies het bedrijf dat het beste bij je past." 
    },
  ];

  const features = [
    {
      icon: Shield,
      title: "Geen kosten, geen verplichtingen",
      description: "Je betaalt niets voor het aanvragen van verhuisoffertes. Pas als je zelf kiest voor een verhuizer, maak je afspraken.",
    },
    {
      icon: Clock,
      title: "Snel schakelen",
      description: "De meeste verhuisbedrijven reageren binnen een werkdag. Zo verlies je geen kostbare tijd aan eindeloos bellen en mailen.",
    },
    {
      icon: Award,
      title: "Alleen gecontroleerde bedrijven",
      description: "Elk verhuisbedrijf op VerhuisMatch is gecontroleerd op KvK-registratie, verzekeringen en klantbeoordelingen.",
    },
  ];

  const fallbackTestimonials = [
    { name: "Jan de Vries", location: "Amsterdam → Utrecht", text: "Binnen een dag 4 verhuisoffertes ontvangen. Uiteindelijk €400 bespaard door te vergelijken!", rating: 5, saved: "€400", hoursAgo: 2 },
    { name: "Maria Bakker", location: "Rotterdam → Den Haag", text: "Super makkelijk. Het verhuisbedrijf was stipt op tijd en heel vriendelijk.", rating: 5, saved: "€250", hoursAgo: 5 },
    { name: "Peter Jansen", location: "Eindhoven → Tilburg", text: "Fijn dat je kunt vergelijken. Goede service en eerlijke prijzen.", rating: 5, saved: "€320", hoursAgo: 8 },
    { name: "Sophie van Dam", location: "Utrecht → Amersfoort", text: "Heel tevreden! Drie verhuisoffertes binnen 24 uur en allemaal professionele bedrijven.", rating: 5, saved: "€280", hoursAgo: 12 },
    { name: "Thomas Mulder", location: "Groningen → Leeuwarden", text: "Mijn verhuizing verliep vlekkeloos. Aanrader voor iedereen die wil verhuizen!", rating: 5, saved: "€350", hoursAgo: 18 },
    { name: "Lisa Hendriks", location: "Haarlem → Amsterdam", text: "Ontzettend handig platform. Snel vergelijken en het beste bedrijf gekozen.", rating: 5, saved: "€420", hoursAgo: 24 },
  ];

  const testimonials = dynamicTestimonials && dynamicTestimonials.length > 0 
    ? dynamicTestimonials 
    : fallbackTestimonials;

  const stats = [
    { value: "15.847", label: "Verhuizingen geregeld", icon: TrendingUp },
    { value: "4.8/5", label: "Gemiddelde score", icon: Star },
    { value: "€340", label: "Gemiddeld bespaard", icon: Heart },
    { value: "< 24u", label: "Reactietijd verhuizers", icon: Clock },
  ];

  const faqs = [
    { question: "Wat kost een verhuizing gemiddeld in 2026?", answer: "De gemiddelde verhuiskosten in 2026 liggen tussen €400 en €2.500, afhankelijk van woningtype, afstand en gewenste services. Een appartement verhuizen kost gemiddeld €400-€800, een eengezinswoning €800-€1.500, en een vrijstaande woning €1.200-€2.500. Door verhuisoffertes te vergelijken via VerhuisMatch kunt u tot 40% besparen op deze kosten." },
    { question: "Hoe lang duurt een verhuizing gemiddeld?", answer: "Een gemiddelde particuliere verhuizing duurt 4 tot 8 uur, afhankelijk van de hoeveelheid spullen en de afstand. Voor een studio of klein appartement rekent u 2-4 uur, voor een gezinswoning 6-8 uur. Internationale verhuizingen kunnen meerdere dagen in beslag nemen." },
    { question: "Wanneer moet ik een verhuisbedrijf boeken?", answer: "Boek uw verhuisbedrijf bij voorkeur 4-6 weken van tevoren, of 8-12 weken in drukke periodes (zomer, einde maand). Last-minute boekingen zijn vaak duurder en hebben minder beschikbaarheid. Vraag tijdig verhuisoffertes aan via VerhuisMatch om de beste prijzen te vergelijken." },
    { question: "Is mijn inboedel verzekerd tijdens de verhuizing?", answer: "Erkende verhuisbedrijven hebben een vervoerdersaansprakelijkheidsverzekering. Standaard zijn uw spullen verzekerd tot €100.000. Controleer altijd de polisvoorwaarden en vraag naar het eigen risico. U kunt ook uw eigen inboedelverzekering tijdelijk uitbreiden voor extra dekking." },
    { question: "Hoe werkt VerhuisMatch precies?", answer: "U vult uw postcode en verhuisgegevens in via ons formulier. Binnen 24 uur ontvangt u tot 5 verhuisoffertes van verhuisbedrijven in uw regio. U vergelijkt de voorstellen en kiest zelf met welk bedrijf u in zee gaat — geheel vrijblijvend en 100% gratis." },
    { question: "Is de service van VerhuisMatch echt gratis?", answer: "Ja, 100% gratis voor u als consument. U betaalt nooit voor het aanvragen of ontvangen van verhuisoffertes. Alleen wanneer u besluit met een verhuisbedrijf samen te werken, betaalt u hen rechtstreeks voor de verhuizing." },
    { question: "Kan ik mijn verhuizing annuleren of uitstellen?", answer: "Ja, maar let op de annuleringsvoorwaarden. De meeste verhuisbedrijven hanteren een gratis annuleringstermijn van 48-72 uur. Bij latere annulering kunnen kosten in rekening worden gebracht, vaak een percentage van de verhuisofferte. Bespreek dit altijd vooraf met uw verhuizer." },
    { question: "Hoe kies ik een betrouwbaar verhuisbedrijf?", answer: "Let op: KvK-registratie, lidmaatschap van een branchevereniging (zoals Erkende Verhuizers), online reviews, en een duidelijke verhuisofferte met alle kosten. Via VerhuisMatch werkt u alleen met geverifieerde bedrijven die aan strenge kwaliteitseisen voldoen." },
    { question: "Heb ik een verhuislift nodig?", answer: "Een verhuislift is nodig bij woningen op 3 of meer verdiepingen zonder ruime lift, bij grote meubels die niet door het trappenhuis passen, of smalle steile trappen. Kosten liggen tussen €150-€400 per dag. Verhuisbedrijven adviseren u hierover na een inspectie." },
    { question: "Hoeveel verhuisoffertes kan ik ontvangen via VerhuisMatch?", answer: "U ontvangt doorgaans 3 tot 5 verhuisoffertes van verschillende verhuisbedrijven. Dit geeft u een goed beeld van de marktprijzen en helpt u de beste keuze te maken voor uw situatie." },
  ];

  return (
    <>
      <SEOHead
        title="Vergelijk Verhuisoffertes & Bespaar tot 40% | VerhuisMatch"
        description="Vergelijk gratis verhuisoffertes van erkende verhuisbedrijven. Ontvang binnen 24 uur tot 5 offertes en bespaar gemiddeld €340."
        canonical="/"
        schemaJson={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "VerhuisMatch",
            "alternateName": "VerhuisMatch.com",
            "url": "https://verhuismatch.com",
            "logo": "https://verhuismatch.com/favicon.png",
            "description": "Vergelijk gratis verhuisoffertes van erkende verhuisbedrijven in Nederland en bespaar tot 40% op uw verhuizing.",
            "foundingDate": "2024",
            "areaServed": { "@type": "Country", "name": "Netherlands" },
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "customer service",
              "email": "info@verhuismatch.com",
              "availableLanguage": ["Dutch", "English"]
            },
            "sameAs": []
          },
          {
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            "name": "VerhuisMatch",
            "url": "https://verhuismatch.com",
            "description": "Vergelijk gratis verhuisoffertes van erkende verhuisbedrijven in Nederland.",
            "areaServed": {
              "@type": "Country",
              "name": "Netherlands"
            },
            "serviceType": ["Verhuisoffertes vergelijken", "Verhuisbedrijven vinden", "Verhuiskosten berekenen"],
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Verhuisdiensten",
              "itemListElement": [
                { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Particuliere verhuizing" } },
                { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Zakelijke verhuizing" } },
                { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Internationale verhuizing" } }
              ]
            },
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "NL"
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "customer service",
              "email": "info@verhuismatch.com",
              "availableLanguage": ["Dutch", "English"]
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "VerhuisMatch",
            "url": "https://verhuismatch.com",
            "description": "Vergelijk verhuisoffertes en bespaar tot 40% op uw verhuizing",
            "publisher": { "@type": "Organization", "name": "VerhuisMatch" },
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://verhuismatch.com/quote?postcode={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "Klantbeoordelingen VerhuisMatch",
            "itemListElement": testimonials.slice(0, 10).map((testimonial, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "item": {
                "@type": "Review",
                "author": { "@type": "Person", "name": testimonial.name },
                "reviewRating": { "@type": "Rating", "ratingValue": testimonial.rating, "bestRating": 5, "worstRating": 1 },
                "reviewBody": testimonial.text,
                "itemReviewed": {
                  "@type": "LocalBusiness",
                  "name": "VerhuisMatch",
                  "address": { "@type": "PostalAddress", "addressCountry": "NL" }
                },
                "datePublished": "2026-01-01"
              }
            }))
          }
        ]}
      >
        <meta name="keywords" content="verhuisoffertes, verhuisofferte vergelijken, verhuisbedrijf vergelijken, verhuisbedrijven nederland, goedkoop verhuizen" />
      </SEOHead>
      <ExitIntentPopup />
      <div className="min-h-screen bg-background">
        <Header />

        {/* 1. Hero — Aandacht trekken + formulier */}
        <HeroSection />

        {/* 2. Vertrouwenscijfers — Direct bewijs */}
        <section className="py-10 border-b border-border bg-muted/20">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <stat.icon className="w-5 h-5 text-primary mx-auto mb-2" />
                  <p className="text-2xl md:text-3xl font-bold text-foreground mb-1">{stat.value}</p>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 3. Hoe het werkt — Duidelijk proces uitleggen */}
        <section className="py-16 md:py-20">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-3">
              Zo werkt het aanvragen van verhuisoffertes
            </h2>
            <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
              Van aanvraag tot verhuisdag — wij koppelen je aan verhuizers die passen bij jouw situatie. Jij kiest, wij regelen de rest.
            </p>

            <div className="max-w-4xl mx-auto">
              <div className="grid md:grid-cols-3 gap-8">
                {steps.map((step) => (
                  <div key={step.number} className="relative text-center">
                    <div className="w-14 h-14 rounded-full bg-primary/10 border-2 border-primary text-primary font-bold text-xl flex items-center justify-center mx-auto mb-4">
                      {step.number}
                    </div>
                    <step.icon className="w-5 h-5 text-primary mx-auto mb-2" />
                    <h3 className="font-bold text-foreground mb-2 text-base">{step.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
                  </div>
                ))}
              </div>

              <div className="text-center mt-10">
                <Button asChild size="lg" className="h-12 px-10 bg-accent hover:bg-accent/90 text-accent-foreground font-semibold rounded-lg">
                  <Link to="/quote">
                    Start mijn aanvraag
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
                <p className="text-xs text-muted-foreground mt-3">Gratis en vrijblijvend — je zit nergens aan vast</p>
              </div>
            </div>
          </div>
        </section>

        {/* 4. Waarom VerhuisMatch — Voordelen benadrukken */}
        <section className="py-16 md:py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-3">
              Waarom verhuisoffertes vergelijken via VerhuisMatch?
            </h2>
            <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
              Wij maken het makkelijk om de juiste verhuizer te vinden — zonder eindeloos zoeken of bellen
            </p>

            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="p-6 rounded-xl bg-card border border-border hover:border-primary/30 transition-colors"
                >
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 5. Verhuiskosten Calculator — Engagement tool */}
        <MovingCostCalculator />

        {/* 6. Verhuisbedrijven per stad — Lokaal vertrouwen */}
        <TopCompaniesSection />

        {/* 7. Ervaringen van klanten — Sociaal bewijs */}
        <section className="py-16 md:py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-3">
              Ervaringen van verhuizers via VerhuisMatch
            </h2>
            <p className="text-center text-muted-foreground mb-12 max-w-xl mx-auto">
              Lees hoe anderen hun verhuizing hebben geregeld en hoeveel zij hebben bespaard
            </p>

            <TestimonialsCarousel testimonials={testimonials} />

            <div className="text-center mt-8">
              <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-card border border-border">
                <div className="flex -space-x-1">
                  {[1,2,3,4,5].map((i) => (
                    <Star key={i} className="w-4 h-4 fill-accent text-accent" />
                  ))}
                </div>
                <span className="text-sm text-muted-foreground">
                  Gemiddeld <strong className="text-foreground">4.8/5</strong> uit 15.847 beoordelingen
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* 8. Populaire steden — SEO + lokaal verkeer */}
        <PopularCitiesSection />

        {/* 9. Veelgestelde vragen — Bezwaren wegnemen */}
        <section className="py-16 md:py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-3">
                Veelgestelde vragen over verhuisoffertes
              </h2>
              <p className="text-center text-muted-foreground mb-10 max-w-xl mx-auto">
                Heb je een vraag? Hieronder vind je antwoorden op de meest gestelde vragen over verhuizen en verhuisoffertes aanvragen.
              </p>

              <Accordion type="single" collapsible className="w-full space-y-2">
                {faqs.map((faq, index) => (
                  <AccordionItem 
                    key={index} 
                    value={`item-${index}`}
                    className="bg-card border border-border rounded-lg px-6 data-[state=open]:border-primary/30"
                  >
                    <AccordionTrigger className="text-left font-semibold hover:no-underline py-4">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground pb-4 leading-relaxed">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* 10. Laatste CTA — Afsluiter */}
        <section className="py-16 md:py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Klaar om verhuisoffertes te vergelijken?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Vul je gegevens in en ontvang binnen 24 uur meerdere verhuisoffertes — gratis en vrijblijvend.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="h-12 px-8 bg-accent hover:bg-accent/90 text-accent-foreground font-semibold rounded-lg">
                  <Link to="/quote">
                    Ontvang gratis verhuisoffertes
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="h-12 px-8 rounded-lg">
                  <Link to="/verhuiskosten">
                    Bekijk verhuiskosten
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default Index;
